const canEmbedImageInCanvas = (image: HTMLImageElement) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return false;
    }

    ctx.drawImage(image, 0, 0);

    return true;
  } catch {
    return false;
  }
};

export const validateImageURL = async (url: string): Promise<boolean> => {
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = url;

  const loaderPromise: Promise<boolean> = new Promise((resolve) => {
    img.onerror = () => resolve(false);

    img.onload = () => resolve(canEmbedImageInCanvas(img));
  });

  const timeoutPromise: Promise<boolean> = new Promise((resolve) => {
    setTimeout(() => resolve(false), 5000);
  });

  return Promise.race([loaderPromise, timeoutPromise]);
};

export const validateVideoURL = async (url: string): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Accept 200 OK and 206 Partial Content as both are valid for video
    if (response.status !== 200 && response.status !== 206) {
      return false;
    }
  } catch (err) {
    // Continue if fetch fails - some servers don't like HEAD requests
  }

  const maxValidationTime = 10000;

  const video = document.createElement('video');
  video.crossOrigin = 'Anonymous';
  video.muted = true;

  video.preload = 'auto';

  let timeoutId: number | null = null;

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    video.pause();
    video.removeAttribute('src');
    video.load();
  };

  const validatePromise = new Promise<boolean>((resolve) => {
    const checkDimensions = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        cleanup();
        resolve(true);
        return true;
      }
      return false;
    };

    video.onloadedmetadata = () => {
      checkDimensions();
    };

    video.onloadeddata = () => {
      checkDimensions();
    };

    video.oncanplay = () => {
      checkDimensions();
    };

    video.onerror = () => {
      cleanup();
      resolve(false);
    };

    timeoutId = window.setTimeout(() => {
      if (!checkDimensions()) {
        cleanup();
        resolve(false);
      }
    }, maxValidationTime);

    video.src = url;
    video.load();
  });

  return validatePromise;
};
